import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import type { CreativeClass } from '@feathr/blackbox';
import { DISPLAY_CREATIVE_CLASSES, META_CREATIVE_CLASSES } from '@feathr/blackbox';
import { Icon, UniversalSelect } from '@feathr/components';

import type { ICreativesFilters } from '../ProjectCreativesPage.types';

import * as styles from './ProjectChannelSelect.css';

interface IProps {
  filters: ICreativesFilters;
  setFilters: (filters: ICreativesFilters) => void;
}

interface IChannelOption {
  id: string;
  name: string;
  icon: IconDefinition;
  classes: CreativeClass[];
}

function getOptions(t: (key: string) => string): IChannelOption[] {
  return [
    {
      id: 'display',
      name: t('Display'),
      icon: faRectangleAd,
      classes: DISPLAY_CREATIVE_CLASSES,
    },
    {
      id: 'meta',
      name: t('Meta'),
      icon: faMeta,
      classes: META_CREATIVE_CLASSES,
    },
  ];
}

function Option(
  props: OptionProps<IChannelOption> | SingleValueProps<IChannelOption>,
): JSX.Element {
  const { data } = props;
  return (
    <Flex align={'center'} gap={'2'}>
      <Icon icon={data.icon} size={4} theme={'sky'} />
      <span>{data.name}</span>
    </Flex>
  );
}

function ChannelOption(props: OptionProps<IChannelOption>): JSX.Element {
  return (
    <components.Option {...props}>
      <Option {...props} />
    </components.Option>
  );
}

function ChannelSingleValue(props: SingleValueProps<IChannelOption>): JSX.Element {
  return (
    <components.SingleValue {...props}>
      <Option {...props} />
    </components.SingleValue>
  );
}

function ProjectChannelSelect({ filters, setFilters }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const options = getOptions(t);

  const selectedOption = options.find((option) => {
    const currentClasses = filters._cls__in;
    if (!currentClasses) {
      return false;
    }
    return (
      currentClasses.length === option.classes.length &&
      option.classes.every((cls) => currentClasses.includes(cls))
    );
  });

  function handleSelect(option: IChannelOption): void {
    setFilters({
      ...filters,
      _cls__in: option.classes,
    });
  }

  function handleClear(): void {
    setFilters({
      ...filters,
      _cls__in: undefined,
    });
  }

  return (
    <UniversalSelect<IChannelOption>
      components={{ Option: ChannelOption, SingleValue: ChannelSingleValue }}
      isClearable={true}
      name={'creative-channel'}
      onClear={handleClear}
      onSelectSingle={handleSelect}
      options={options}
      placeholder={t('Channel')}
      value={selectedOption}
      wrapperClassName={styles.filter}
    />
  );
}

export default ProjectChannelSelect;
